// What a screen shows while it is waiting, and when it cannot show the thing.
//
// Three states rather than two. A page that renders nothing while loading looks
// broken; a page that renders zeroes while loading looks like a bill of nothing,
// which on a billing screen is worse than looking broken. So loading has a shape
// of its own, and it is the shape of the content it is standing in for.
//
// The failure state repeats what the service said. "Something went wrong" is a
// message that tells a person only that the page has given up — whereas the
// backend's own refusal wording is the one sentence that might tell them whether
// to sign in again, wait, or call somebody.

import { Card, CardContent } from '@miq/design-system/components/ui/card';
import { Skeleton } from '@miq/design-system/components/ui/skeleton';

/** A card-shaped placeholder, so the layout does not jump when data lands. */
export function LoadingCard({ lines = 3 }: { lines?: number }) {
  return (
    <Card>
      <CardContent className="space-y-3 py-6">
        {Array.from({ length: lines }, (_, index) => (
          <Skeleton
            key={index}
            // Descending widths read as text rather than as bars.
            className="h-4"
            style={{ width: `${100 - index * 12}%` }}
          />
        ))}
      </CardContent>
    </Card>
  );
}

/** A single stat tile's placeholder. */
export function LoadingTile() {
  return (
    <Card>
      <CardContent className="space-y-2 pt-5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-7 w-32" />
        <Skeleton className="h-3 w-40" />
      </CardContent>
    </Card>
  );
}

/**
 * The service refused, or could not be reached, and this is what it said.
 *
 * `what` names the thing that could not be loaded, because a page showing two
 * of these needs to say which half is missing.
 */
export function FailedToLoad({ what, error }: { what: string; error: string }) {
  return (
    <Card className="border-destructive/40 bg-destructive/5">
      <CardContent className="py-6">
        <div className="text-sm font-semibold">{what} could not be loaded</div>
        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
      </CardContent>
    </Card>
  );
}

/**
 * There is nothing to show, and that is the true answer rather than a failure.
 *
 * Kept distinct from the failure above on purpose: a customer with no usage yet
 * and a customer whose billing read was refused must not see the same card.
 */
export function NothingYet({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-6 py-10 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}
